import delay from './delay';
// eslint-disable-next-line import/no-cycle
import {
  ReadwiseFetchNetworkConnectivityError,
  ReadwiseFetchServerError,
} from './Errors';
import makeLogger from './makeLogger';

const logger = makeLogger(__filename);

export interface RetryOptions {
  maxAttempts?: number;
  initialDelay?: number;
  multiplier?: number;
  maxDelay?: number;
  signal?: AbortSignal;
}

function shouldRetryError(error: Error): boolean {
  if (error.name === 'AbortError') {
    return false;
  }

  if (error instanceof ReadwiseFetchNetworkConnectivityError) {
    return true;
  }

  if (error instanceof ReadwiseFetchServerError) {
    return true;
  }

  return false;
}

export async function retryNetworkRequestWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const {
    maxAttempts = 3,
    initialDelay = 1000,
    multiplier = 2,
    maxDelay = 30000,
    signal,
  } = options;

  let currentDelay = initialDelay;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      if (signal?.aborted) {
        throw new Error('AbortError');
      }

      return await fn();
    } catch (error) {
      const isLastAttempt = attempt === maxAttempts - 1;

      if (isLastAttempt) {
        throw error;
      }

      if (error instanceof Error && !shouldRetryError(error)) {
        logger.debug('Not retrying error', {
          errorName: error.name,
          errorMessage: error.message,
          attempt: attempt + 1,
        });
        throw error;
      }

      logger.debug('Retrying after error', {
        errorName: error instanceof Error ? error.name : 'Unknown',
        errorMessage: error instanceof Error ? error.message : String(error),
        attempt: attempt + 1,
        nextDelayMs: currentDelay,
        maxAttempts,
      });

      await delay(currentDelay);

      currentDelay = Math.min(currentDelay * multiplier, maxDelay);
    }
  }

  throw new Error('Retry logic failed unexpectedly');
}
